import { playerProfileService } from './playerMasterService';

/**
 * Unified Player Import Service
 * Supports imports from Transfermarkt, Sofascore, and Soccerway
 * Automatically detects the source from URL format
 */

const SOURCE_PATTERNS = {
  transfermarkt: {
    name: 'Transfermarkt',
    patterns: [
      /^https?:\/\/(www\.)?transfermarkt\.[a-z.]+\/[^\/]+\/profil\/spieler\/\d+/i,
      /^https?:\/\/(www\.)?transfermarkt\.[a-z.]+\/[^\/]+\/profile\/player\/\d+/i
    ],
    extractId: (url) => {
      const match = url?.match(/\/(spieler|player)\/(\d+)/);
      return match ? match?.[2] : null;
    },
    extractName: (url) => {
      const match = url?.match(/\/([^\/]+)\/(profil|profile)\/(spieler|player)/);
      return match ? match?.[1]?.replace(/-/g, ' ') : null;
    }
  },
  sofascore: {
    name: 'Sofascore',
    patterns: [
      /^https?:\/\/(www\.)?sofascore\.com\/player\/[^\/]+\/\d+/i,
      /^https?:\/\/(www\.)?sofascore\.com\/[a-z-]+\/player\/[^\/]+\/\d+/i,
      /^https?:\/\/(www\.)?sofascore\.com\/football\/player\/[^\/]+\/\d+/i
    ],
    extractId: (url) => {
      const match = url?.match(/\/player\/[^\/]+\/(\d+)/);
      return match ? match?.[1] : null;
    },
    extractName: (url) => {
      const match = url?.match(/\/player\/([^\/]+)\/\d+/);
      return match ? match?.[1]?.replace(/-/g, ' ') : null;
    }
  },
  soccerway: {
    name: 'Soccerway',
    patterns: [
      /^https?:\/\/(www\.)?(int\.|[a-z]{2}\.)?soccerway\.com\/players\/[^\/]+\/\d+/i,
      /^https?:\/\/(www\.)?(int\.|[a-z]{2}\.)?soccerway\.com\/[a-z-]+\/players\/[^\/]+\/\d+/i
    ],
    extractId: (url) => {
      const match = url?.match(/\/players\/[^\/]+\/(\d+)/);
      return match ? match?.[1] : null;
    },
    extractName: (url) => {
      const match = url?.match(/\/players\/([^\/]+)\/\d+/);
      return match ? match?.[1]?.replace(/-/g, ' ') : null;
    }
  }
};

export const playerImportService = {
  /**
   * Detect the source from URL
   */
  detectSource(url) {
    if (!url || typeof url !== 'string') {
      return null;
    }

    const trimmedUrl = url?.trim();

    for (const [sourceKey, sourceConfig] of Object.entries(SOURCE_PATTERNS)) {
      for (const pattern of sourceConfig?.patterns) {
        if (pattern?.test(trimmedUrl)) {
          return sourceKey;
        }
      }
    }

    return null;
  },

  /**
   * Validate URL format
   */
  isValidUrl(url) {
    return this.detectSource(url) !== null;
  },

  /**
   * Get source name from URL
   */
  getSourceName(url) {
    const source = this.detectSource(url);
    return source ? SOURCE_PATTERNS?.[source]?.name : null;
  },

  /**
   * Extract player ID from URL based on detected source
   */
  extractPlayerId(url) {
    try {
      const source = this.detectSource(url);
      if (!source) return null;

      return SOURCE_PATTERNS?.[source]?.extractId(url);
    } catch (error) {
      console.error('Error extracting player ID:', error);
      return null;
    }
  },

  /**
   * Extract player name from URL based on detected source
   */
  extractPlayerName(url) {
    try {
      const source = this.detectSource(url);
      if (!source) return null;

      return SOURCE_PATTERNS?.[source]?.extractName(url);
    } catch (error) {
      console.error('Error extracting player name:', error);
      return null;
    }
  },

  /**
   * Parse URL and extract basic information
   */
  async parseUrl(url) {
    const source = this.detectSource(url);
    
    if (!source) {
      throw new Error('Invalid URL format. Supported sources: Transfermarkt, Sofascore, Soccerway');
    }

    const playerId = this.extractPlayerId(url);
    const playerName = this.extractPlayerName(url);

    if (!playerId || !playerName) {
      throw new Error('Could not extract player information from URL');
    }

    return {
      source,
      sourceName: SOURCE_PATTERNS?.[source]?.name,
      playerId,
      playerName,
      originalUrl: url,
      dataSource: source
    };
  },

  /**
   * Import player from any supported source URL
   */
  async importPlayer(url) {
    try {
      const parsedData = await this.parseUrl(url);
      
      // Create player profile with extracted data
      const playerData = {
        fullName: parsedData?.playerName || 'Unknown Player',
        knownAs: parsedData?.playerName || '',
        dateOfBirth: '1990-01-01', // Placeholder - would be scraped
        nationalityPrimary: 'Unknown', // Placeholder - would be scraped
        preferredFoot: 'right', // Placeholder - would be scraped
        primaryPosition: 'striker', // Placeholder - would be scraped
        currentClub: 'Unknown', // Placeholder - would be scraped
        dataSource: parsedData?.source,
        // Store the source URL and ID for reference
        placeOfBirth: `${parsedData?.sourceName} ID: ${parsedData?.playerId}`,
      };

      const result = await playerProfileService?.create(playerData);
      return {
        success: true,
        player: result?.data,
        source: parsedData?.sourceName,
        message: `Player imported successfully from ${parsedData?.sourceName}. Please update the profile with complete information.`
      };
    } catch (error) {
      console.error('Import error:', error);
      throw error;
    }
  },

  /**
   * Update existing player with data from URL
   */
  async updatePlayerFromUrl(playerId, url) {
    try {
      const parsedData = await this.parseUrl(url);
      
      // Update player profile with extracted data
      const updateData = {
        dataSource: parsedData?.source,
        placeOfBirth: `${parsedData?.sourceName} ID: ${parsedData?.playerId}`,
      };

      const result = await playerProfileService?.update(playerId, updateData);
      return {
        success: true,
        player: result?.data,
        source: parsedData?.sourceName,
        message: `Player updated with ${parsedData?.sourceName} reference. Please complete the profile manually.`
      };
    } catch (error) {
      console.error('Update error:', error);
      throw error;
    }
  },

  /**
   * Get example URLs for all supported sources
   */
  getExampleUrls() {
    return {
      transfermarkt: [
        'https://www.transfermarkt.com/mohamed-salah/profil/spieler/148455',
        'https://www.transfermarkt.de/erling-haaland/profil/spieler/418560'
      ],
      sofascore: [
        'https://www.sofascore.com/player/kylian-mbappe/903339',
        'https://www.sofascore.com/football/player/erling-haaland/939956'
      ],
      soccerway: [
        'https://int.soccerway.com/players/mohamed-salah/148455/',
        'https://us.soccerway.com/players/kylian-mbappe/342229/'
      ]
    };
  }
};

export default playerImportService;